/* eslint-disable max-len */
import React, {type RefObject} from 'react';

/**
 * The `GeoProps` interface defines the shape of the properties object that is expected for this component.
 * It outlines the required properties that needs to be provided when utilizing this component expecting an object of this type.
 */
interface GeoProps {
    /**
     * A string representing the CSS class to be applied to the GeoIcon element to override its default styling.
     *
     * @default ''
     */
    className?: string;
    /**
     * A string representing the color to be applied.
     *
     * @default '#282828'
     */
    color1?: string;
    /**
     * A number or string representing the height of the GeoIcon element.
     *
     * @default 23
     */
    height?: number | string;
    /**
     * The `ref` property represents a reference to the SVG element, allowing for direct manipulation or access to the DOM node.
     */
    ref?: RefObject<SVGSVGElement | null>;
    /**
     * The `testId` property represents a unique identifier, usually in the form of a string, assigned to a component for testing purposes.
     * This property is crucial for uniquely identifying components during testing, allowing for more accurate and reliable tests.
     *
     * @default 'Geo'
     */
    testId?: string;
    /**
     * A number or string representing the height of the GeoIcon element.
     *
     * @default 17
     */
    width?: number | string;
}

/**
 * The `Geo` component is a functional component designed to render a customizable SVG element representing an Icon.
 * It receives several props through the `GeoProps` interface, allowing for customization of class, color, dimensions, and testing identifier.
 *
 * @param props           The component props.
 * @param props.className A string representing the CSS class to be applied to the GeoIcon element to override its default styling.
 * @param props.color1    A string representing the color to be applied to the Icon.
 * @param props.height    A number or string representing the height of the GeoIcon element.
 * @param props.testId    A string representing a unique identifier assigned to the component for testing purposes.
 * @param props.width     A number or string representing the height of the GeoIcon element.
 * @param props.ref       A reference to the SVG element, allowing for direct manipulation or access to the DOM node.
 * @returns A React element representing the `Check` component with the specified properties.
 *
 * @example
 * ```tsx
 * const GeoComponent = <Geo className="myClass" color1="#000" height="20" testId="myTestId" width="20" />;
 * ```
 */
export const Geo = ({
    className = '',
    color1 = '#282828',
    height = 23,
    ref,
    testId = 'Geo',
    width = 17
}: GeoProps) => (
    <svg
        ref={ref}
        className={className}
        data-cy={testId}
        height={height}
        viewBox="0 0 17 23"
        width={width}
        xmlns="http://www.w3.org/2000/svg"
        xmlnsXlink="http://www.w3.org/1999/xlink"
    >
        <path d="M8.2.1c4.47 0 8.1 3.73 8.1 8.31 0 2.87-1.43 5.93-3.06 8.48a40 40 0 0 1-4.36 5.57 1 1 0 0 1-.68.3 1 1 0 0 1-.68-.3c-.87-.93-2.72-3-4.36-5.57C1.53 14.34.1 11.3.1 8.41A8.2 8.2 0 0 1 8.2.11Zm0 1.14A7.1 7.1 0 0 0 1.24 8.4c0 2.66 1.41 5.6 3.01 8.05a39 39 0 0 0 3.95 5.06c.72-.77 2.41-2.68 3.95-5.06 1.6-2.46 3.01-5.4 3.01-8.05A7.1 7.1 0 0 0 8.2 1.24Z" fill={color1} stroke={color1} strokeWidth=".2" />
        <path d="M8.2 4.02c2.4 0 4.33 1.98 4.33 4.41s-1.94 4.41-4.33 4.41a4.4 4.4 0 0 1-4.33-4.4A4.4 4.4 0 0 1 8.2 4.01Zm0 1.14A3.24 3.24 0 0 0 5 8.43c0 1.8 1.44 3.28 3.2 3.28s3.2-1.47 3.2-3.28-1.44-3.27-3.2-3.27Z" fill={color1} stroke={color1} strokeWidth=".2" />
    </svg>
);

Geo.displayName = 'Geo';